// M005-F0056 (M005-D005 §9) — "New booking" entry point on the Bookings Hub.
//
// LEAD DECISION 2 (binding, 2026-09-07): the New-booking entry reuses the
// EXISTING booking-creation flow by ONE-TIME DUPLICATION of the entry point
// only — no shared-component extraction in this wave (TODO-D005-1 records
// the de-duplication follow-up as non-blocking). Everything below is copied
// verbatim from src/components/event-simple/EventSimpleDashboard.js:
//   - generateTimeOptions / generateEndTimeOptions / TIME_OPTIONS
//     (M004-F0075/F0076),
//   - the form state shape and handleInputChange's end-time auto-set,
//   - validateBooking (unchanged rule-for-rule — NO new validation logic),
//   - the POST /api/bookings payload (Core booking.routes.ts:346,361 —
//     gated `bookings_create`), lifted verbatim into the exported
//     buildCreateBookingPayload so the jest unit can pin the shape,
//   - the booking-form JSX.
// This file NEVER imports from event-simple/, and the frozen simple view
// (EventSimpleDashboard.js / EventSimple.css / the /app/event-simple route)
// stays byte-identical (eventSimpleRegressionGuard.test.js).
//
// Gating (WEB-BKHUB-010): the entry renders only when the hub's SELECTED
// organisation my-permissions projection holds bookings_create === true —
// permission-driven, never role-name-driven. Tri-state 'partial', missing
// keys and failed reads (the projection helper resolves errors to {}) all
// fail closed: no action, never a guess.
import React, { useEffect, useState } from 'react';
import { bookingApi, roomApi } from '../../services/api';

// M004-F0075: Generate start time options in 15-minute intervals (6am to 10pm)
const generateTimeOptions = () => {
  const options = [];
  // Start at 6am (6), end at 10pm (22) to allow at least 1hr booking before 11pm cutoff
  for (let hour = 6; hour <= 22; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      const displayHour = hour > 12 ? hour - 12 : hour;
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayTime = `${displayHour}:${minute.toString().padStart(2, '0')} ${ampm}`;
      options.push({ value: time, label: displayTime });
    }
  }
  return options;
};

// M004-F0076: Generate end time options - must be after start time, in 1-hour increments, max 11pm
const generateEndTimeOptions = (startTime) => {
  if (!startTime) return [];

  const [startHour, startMin] = startTime.split(':').map(Number);
  const startTotalMinutes = startHour * 60 + startMin;
  const maxEndHour = 23; // 11pm max
  const options = [];

  // Generate options in 1-hour increments from start time
  for (let hours = 1; hours <= 6; hours++) {
    const endTotalMinutes = startTotalMinutes + (hours * 60);
    const endHour = Math.floor(endTotalMinutes / 60);
    const endMin = endTotalMinutes % 60;

    // Don't go past 11pm (23:00)
    if (endHour > maxEndHour) break;

    const timeStr = `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`;
    const displayHour = endHour > 12 ? endHour - 12 : endHour;
    const ampm = endHour >= 12 ? 'PM' : 'AM';
    const displayStr = `${displayHour}:${endMin.toString().padStart(2, '0')} ${ampm} (${hours}hr)`;
    options.push({ value: timeStr, label: displayStr });
  }

  return options;
};

const TIME_OPTIONS = generateTimeOptions();

export const BOOKINGS_CREATE_PERMISSION_KEY = 'bookings_create';

// §9: exactly the capability key Core's POST /api/bookings gate checks
// (assertOrganisationCapability(..., 'bookings_create')). Only an explicit
// `true` grants — this is the same fail-closed reading bookingsHubPermissions
// applies to the tab set.
export const canCreateBookings = (permissions) =>
  permissions?.[BOOKINGS_CREATE_PERMISSION_KEY] === true;

// The payload the existing flow POSTs, lifted verbatim out of the original
// handler (same room_id parseInt, same 'Event Booking' title fallback, same
// `${date}T${time}:00` strings, same 'one-off' type) so the duplicated entry
// cannot drift from what Core's booking.routes.ts expects.
export const buildCreateBookingPayload = (formData) => ({
  room_id: parseInt(formData.roomId),
  title: formData.purpose || 'Event Booking',
  description: formData.purpose,
  start_time: `${formData.date}T${formData.startTime}:00`,
  end_time: `${formData.date}T${formData.endTime}:00`,
  booking_type: 'one-off'
});

const NewBookingEntry = ({ permissions, onCreated }) => {
  const [rooms, setRooms] = useState([]);
  // Form state — same shape the simple view's form uses.
  const [formData, setFormData] = useState({
    roomId: '',
    date: '',
    startTime: '',
    endTime: '',
    purpose: ''
  });
  const [formError, setFormError] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Mount-time room read — the same roomApi.getRooms() call (and the same
  // console.error-and-continue error behaviour) as the simple view's
  // fetchRooms. Made only when the entry can actually render (§9 gating): a
  // denied or fail-closed projection must not produce the request at all.
  useEffect(() => {
    if (!canCreateBookings(permissions)) return undefined;
    let current = true;
    roomApi.getRooms()
      .then((response) => {
        if (current) setRooms(response.data?.data || response.data || []);
      })
      .catch((err) => {
        console.error('Failed to fetch rooms:', err);
      });
    return () => { current = false; };
  }, [permissions]);

  // M004-F0076: Handle form input change - auto-set endTime when startTime changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'startTime') {
      // Auto-set endTime to 1 hour after startTime (default duration)
      const [startHour, startMin] = value.split(':').map(Number);
      const endTotalMinutes = (startHour * 60 + startMin) + 60; // Add 1 hour
      const endHour = Math.floor(endTotalMinutes / 60);
      const endMin = endTotalMinutes % 60;

      // Only set if within valid range (max 11pm)
      let newEndTime = '';
      if (endHour <= 23) {
        newEndTime = `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`;
      }

      setFormData(prev => ({ ...prev, [name]: value, endTime: newEndTime }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    setFormError('');
  };

  // M004-F0076: End time options based on selected start time
  const endTimeOptions = generateEndTimeOptions(formData.startTime);

  // Validate booking form — verbatim copy of the original validateBooking.
  const validateBooking = () => {
    if (!formData.roomId) return 'Please select a room';
    if (!formData.date) return 'Please select a date';
    if (!formData.startTime) return 'Please select start time';
    if (!formData.endTime) return 'Please select end time';

    // Validate 15-minute intervals
    const startMinutes = parseInt(formData.startTime.split(':')[1], 10);
    const endMinutes = parseInt(formData.endTime.split(':')[1], 10);
    if (startMinutes % 15 !== 0) {
      return 'Start time must be on a 15-minute interval (00, 15, 30, or 45)';
    }
    if (endMinutes % 15 !== 0) {
      return 'End time must be on a 15-minute interval (00, 15, 30, or 45)';
    }

    const selectedDate = new Date(formData.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      return 'Cannot book past dates';
    }

    if (formData.endTime <= formData.startTime) {
      return 'End time must be after start time';
    }

    // Calculate duration in minutes
    const start = new Date(`2000-01-01 ${formData.startTime}`);
    const end = new Date(`2000-01-01 ${formData.endTime}`);
    const duration = (end - start) / (1000 * 60);

    if (duration < 30) {
      return 'Minimum booking duration is 30 minutes';
    }

    if (duration > 480) {
      return 'Maximum booking duration is 8 hours';
    }

    return null;
  };

  // Handle create booking — verbatim copy of the original handleCreateBooking
  // except: the payload comes from buildCreateBookingPayload (the identical
  // object literal, lifted) and the post-success refresh calls the hub's
  // onCreated (the hub re-reads /api/bookings so the new booking appears in
  // the actor's own Upcoming tab immediately, §9) instead of the simple
  // view's fetchBookings.
  const handleCreateBooking = async (e) => {
    e.preventDefault();

    const validationError = validateBooking();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setFormSubmitting(true);
    setFormError('');

    try {
      // Backend expects snake_case
      await bookingApi.createBooking(buildCreateBookingPayload(formData));

      // Reset form
      setFormData({
        roomId: '',
        date: '',
        startTime: '',
        endTime: '',
        purpose: ''
      });

      // Refresh bookings
      if (onCreated) await onCreated();
      alert('Booking created successfully and awaiting admin approval');
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to create booking');
    } finally {
      setFormSubmitting(false);
    }
  };

  // WEB-BKHUB-010: hide unless the SELECTED organisation's projection grants
  // bookings_create. After the hooks above, so hook order stays stable.
  if (!canCreateBookings(permissions)) return null;

  return (
    <section className="booking-form-section bkh-new-booking" data-testid="bkh-new-booking">
      <h2>➕ Book a Room</h2>
      <form onSubmit={handleCreateBooking} className="booking-form" data-testid="bkh-new-booking-form">
        {formError && <div className="alert alert-error">{formError}</div>}

        <div className="form-group">
          <label htmlFor="roomId">Room *</label>
          <select
            id="roomId"
            name="roomId"
            value={formData.roomId}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Room</option>
            {rooms.map(room => (
              <option key={room.id} value={room.id}>
                {room.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="date">Date *</label>
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              min={new Date().toISOString().split('T')[0]}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="startTime">Start Time *</label>
            <select
              id="startTime"
              name="startTime"
              value={formData.startTime}
              onChange={handleInputChange}
              required
            >
              <option value="">Select start time</option>
              {TIME_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* M004-F0076: End time in 1-hour increments */}
          <div className="form-group">
            <label htmlFor="endTime">End Time * <small>(1hr increments)</small></label>
            <select
              id="endTime"
              name="endTime"
              value={formData.endTime}
              onChange={handleInputChange}
              required
              disabled={!formData.startTime}
            >
              <option value="">{formData.startTime ? 'Select duration...' : 'Select start time first'}</option>
              {endTimeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="purpose">Purpose</label>
          <textarea
            id="purpose"
            name="purpose"
            value={formData.purpose}
            onChange={handleInputChange}
            placeholder="What is this booking for?"
            rows="3"
          />
        </div>

        <button
          type="submit"
          className="btn btn-primary"
          disabled={formSubmitting}
          data-testid="bkh-new-booking-submit"
        >
          {formSubmitting ? 'Creating...' : 'Create Booking'}
        </button>
      </form>
    </section>
  );
};

export default NewBookingEntry;
